import { useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { MdFolder, MdWarning } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { Button, Icon, Modal, ModalFooter } from '@nbirl/nbi-common-ui-libs';
import classNames from 'classnames';
import { uniqueId as _uniqueId } from 'lodash';

import styles from './bulk-upload-modal.module.css';

import { PATHS } from '../../../../../shared/constants';
import { useAppConfig, useBulkUploads } from '../../../../../shared/context';
import { BulkUploadActionType, UploadStatus } from '../../../../../shared/enum';
import {
  QueuedUpload,
  RejectedUpload,
  UploadError,
} from '../../../../../shared/models';
import {
  fileSizeValidator,
  pluralizeString,
} from '../../../../../shared/utils';

type BulkUploadModalProps = {
  folderId: string;
  isOpen: boolean;
  path: string;
  onDismiss: () => void;
};

export const BulkUploadModal = ({
  folderId,
  isOpen,
  path,
  onDismiss,
}: BulkUploadModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const { uploadFileSizeMbLimit, maxBulkUploadFilesLimit } = useAppConfig();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const { dispatch } = useBulkUploads();

  const { getInputProps, getRootProps } = useDropzone({
    multiple: true,
    maxFiles: maxBulkUploadFilesLimit,
    validator: (file) => fileSizeValidator(file, uploadFileSizeMbLimit),
    onDropAccepted: setAcceptedFiles,
    onDropRejected: setRejectedFiles,
  });

  const handleCloseModal = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setAcceptedFiles([]);
    setRejectedFiles([]);
    onDismiss();
  };

  const handleConfirmUpload = () => {
    const queuedUploads: QueuedUpload[] = acceptedFiles.map((file) => ({
      file,
      folderId,
      path,
      id: _uniqueId('upload'),
      status: UploadStatus.QUEUED,
    }));

    const rejectedUploads: RejectedUpload[] = rejectedFiles.map(
      (rejection) => ({
        file: rejection.file,
        folderId,
        path,
        id: _uniqueId('upload'),
        status: UploadStatus.REJECTED,
        error: rejection.errors[0] as UploadError,
      })
    );

    dispatch({
      type: BulkUploadActionType.ADD_UPLOADS,
      payload: { queued: queuedUploads, rejected: rejectedUploads },
    });
    handleCloseModal();
    history.push(PATHS.Uploads);
  };

  const filesToUploadCount = acceptedFiles.length + rejectedFiles.length;

  return (
    <Modal
      heading="Upload Files"
      headingIcon={<Icon icon={<MdFolder />} />}
      isOpen={isOpen}
      onDismiss={handleCloseModal}
    >
      <div
        {...getRootProps()}
        className={classNames(
          'flex flex-col items-center p-12 bg-primary-25 rounded-lg cursor-pointer',
          styles.upload
        )}
      >
        <input
          ref={inputRef}
          {...getInputProps()}
          data-testid="dropzone-input"
        />

        {filesToUploadCount === 0 ? (
          <>
            <p className="text-gray-400">
              Drop files here or{' '}
              <span className="text-primary-500 font-medium">browse</span>
            </p>
            <p className="text-gray-400 font-medium">Max file size: 1 GB</p>
            <p className="text-gray-400 font-medium">
              Max number of files: {maxBulkUploadFilesLimit}
            </p>
          </>
        ) : (
          <>
            <p>
              {filesToUploadCount} {pluralizeString('file', filesToUploadCount)}{' '}
              selected for upload
            </p>
            {filesToUploadCount > maxBulkUploadFilesLimit && (
              <div className="flex items-center space-x-2 mt-3">
                <Icon icon={<MdWarning className="text-red-800" />} />
                <p className="text-red-800 flex">
                  Maximum of {maxBulkUploadFilesLimit} files uploads exceeded
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={handleCloseModal}>
          Cancel
        </Button>

        <Button
          variant="emerald"
          disabled={
            filesToUploadCount === 0 ||
            filesToUploadCount > maxBulkUploadFilesLimit
          }
          onClick={handleConfirmUpload}
        >
          Confirm Upload
        </Button>
      </ModalFooter>
    </Modal>
  );
};
